import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Avatar from '@mui/joy/Avatar';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';

import { toast } from 'react-toastify';
import Modal from '@mui/joy/Modal';

import MenuImprovePost from '@/components/Posts/MenuImprovePost';
import CircularProgress from '@mui/joy/CircularProgress';
import Link from '@mui/joy/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import Textarea from '@mui/joy/Textarea';
import LoaderIcon from "react-loader-icon";
import useBell from '@/utils/useBell';
import Dropdown from '@mui/joy/Dropdown';
import MenuButton from '@mui/joy/MenuButton';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';
import Apps from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import { sanitizePost } from "@/utils/sanitizePost";


const ModalOptimize = ({
    isOptimizeModalOpen,
    optimizeCloseModal,
    session,
    history,
    setHistory,
    messages,
    setMessages,
    optimizeLoading,
    handleOptimize,
    content,
    setContent,
    setIsOptimizeModalOpen,
    setOptimizeLoading,
}) => {
    const [userInput, setUserInput] = useState("");
    const [loading, setLoading] = useState(false);

    const messageListRef = useRef(null);
    const textAreaRef = useRef(null);
    const { playBellSound } = useBell();

    const clearHtmlFromText = (text) => {
        const regex = /(<([^>]+)>)/gi;
        return text.replace(regex, "");
    }


    // Auto scroll chat to bottom
    useEffect(() => {
        const messageList = messageListRef.current;
        if (messageList) {
            messageList.scrollTop = messageList.scrollHeight;
        }
    }, [messages]);

    // Focus on text field on load
    useEffect(() => {
        const textArea = textAreaRef.current;
        if (textArea) {
            textArea.focus();
        }
    }, []);

    // Prevent blank submissions and allow for multiline input
    const handleEnter = (e) => {
        if (e.key === "Enter" && userInput) {
            if (!e.shiftKey && userInput) {
                handleSubmit(e);
            }
        } else if (e.key === "Enter") {
            e.preventDefault();
        }
    };

    // Handle errors
    const handleError = () => {
        playBellSound();
        setMessages((prevMessages) => [...prevMessages, { "message": "Oops! There seems to be an error. Please try again.", "type": "apiMessage" }]);
        setOptimizeLoading(false);
        setUserInput("");
    }

    const copyToClipboard = (text) => {
        const trimmedText = text.trim();
        navigator.clipboard.writeText(trimmedText);

        toast("Text has been copied to clipboard!");
    }

    const editAndPost = (text) => {
        const trimmedText = sanitizePost(text.trim());
        setContent(trimmedText);
        setIsOptimizeModalOpen(false);
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (userInput.trim() === "") {
            return;
        }

        setLoading(true);
        setMessages((prevMessages) => [...prevMessages, { "message": userInput, "type": "userMessage" }]);

        // Send user question and history to API
        const onlyLast10messages = messages.slice(Math.max(messages.length - 10, 0));

        const response = await fetch("/api/posts/optimize_custom", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ post: clearHtmlFromText(content), question: userInput, history: onlyLast10messages }),
        });

        if (response.status !== 200) {
            handleError();
            return;
        }

        // Reset user input
        setUserInput("");

        if (response.body) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            // Create an initial API message entry for the streamed response
            setMessages(prevMessages => [...prevMessages, { "message": "", "type": "apiMessage" }]);
            const apiMessageIndex = messages.length; // Index of the API message

            reader.read().then(function processText({ done, value }) {
                if (done) {
                    setLoading(false);
                    playBellSound();
                    return;
                }

                // Decode the chunk and append it to the API message
                const chunk = decoder.decode(value, { stream: true });

                // Append chunk to the latest apiMessage
                setMessages(prevMessages => {
                    const newMessages = [...prevMessages];
                    const apiMessageIndex = newMessages.findIndex((msg, index) => msg.type === "apiMessage" && index >= newMessages.length - 1);
                    if (apiMessageIndex !== -1) {
                        newMessages[apiMessageIndex].message += chunk;
                    }
                    return newMessages;
                });

                // Read the next chunk
                reader.read().then(processText);
            });
        }
    };


    // Keep history in sync with messages
    useEffect(() => {
        setHistory(messages.map((message) => message.message));
    }, [messages]);


    return (
        <Modal open={isOptimizeModalOpen} onClose={optimizeCloseModal} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999999999 }}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: 500,
                    minWidth: { xs: '100%', sm: 700, md: 700, lg: 700, },
                    borderRadius: 'md',
                    p: 0,
                    boxShadow: 'lg',
                    backgroundColor: 'white',
                }}
            >

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <CloseIcon onClick={optimizeCloseModal} sx={{ cursor: 'pointer' }} />
                </Box>

                <Box sx={{ padding: '1rem' }}>
                    <Box
                        ref={messageListRef}
                        sx={{ height: '200px', overflowY: 'scroll', border: '1px solid #e2e8f0', padding: '1rem', minHeight: '50vh' }}
                    >
                        {messages.map((message, index) => {
                            return (
                                <div key={index}>
                                    <Box sx={{ marginTop: 1 }} className={message.type === "apiMessage" ? 'chatStyleSystem' : 'chatStyleUser'}>
                                        <Box
                                            key={index}
                                            sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', marginTop: '0rem' }}
                                        >
                                            {message.type === "apiMessage" ?
                                                <Avatar
                                                    sx={{ width: '24px', height: '24px' }}
                                                    src="/images/logo-icon.png"
                                                    alt="Scriptu"
                                                />
                                                :
                                                <Avatar
                                                    sx={{ width: '24px', height: '24px' }}
                                                    src={session?.settings?.profile_image}
                                                    alt="User Avatar"
                                                />
                                            }

                                            <Box sx={{ marginTop: 1 }}>
                                                <Typography key={index} sx={{
                                                    color: 'grey.600',
                                                    marginLeft: '10px',
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    textTransform: 'capitalize',
                                                    marginBottom: '5px',
                                                }}>
                                                    {message.type === "apiMessage" ? "Scriptu" : session?.session?.user?.name}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Typography key={index}>
                                                <div dangerouslySetInnerHTML={{
                                                    __html: message.message.replace(/(?:\r\n|\r|\n)/g, '<br />')
                                                }} />
                                            </Typography>

                                            {message.type === "apiMessage" && (!loading && !optimizeLoading) && (
                                                <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', marginTop: '1rem' }}>
                                                    <Link href="#" onClick={() => copyToClipboard(sanitizePost(message.message))} variant="plain" sx={{ marginRight: '8px' }}>
                                                        <ContentCopyIcon sx={{ marginRight: '5px', fontSize: '17px' }} />
                                                        Copy
                                                    </Link>
                                                    <Link href="#" onClick={() => editAndPost(message.message)} variant="plain" sx={{ marginRight: '8px' }}>
                                                        <ModeEditIcon sx={{ marginRight: '5px', fontSize: '17px' }} />
                                                        Edit and Post
                                                    </Link>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </div>
                            )
                        })}


                        {(loading || optimizeLoading) && (
                            <Box sx={{ marginTop: 3 }}>
                                <LoaderIcon type={"bubbles"} size={50} style={{ float: 'left' }} />
                            </Box>
                        )}

                    </Box>

                    <Box sx={{ marginTop: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', marginTop: '1rem', marginBottom: '2rem' }}>
                            <Typography sx={{ fontSize: '.875rem', fontWeight: '700', marginRight: '15px' }}>Optimize Your Post:</Typography>


                            <Dropdown>
                                <MenuButton startDecorator={<Apps />} disabled={optimizeLoading || loading}>
                                    Improve
                                </MenuButton>
                                <Menu placement="bottom-end">
                                    <Menu sx={{ zIndex: 10000000000, maxHeight: '150px' }}>
                                        <MenuItem onClick={() => handleOptimize('COPYRIGHT_TIPS', history)}>Copywriting Tips</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('KEEP_WRITING', history)}>Keep Writing</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('ADD_EMOJIS', history)}>Add Emojis</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('SHORTER', history)}>Make Shorter</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('EXPAND', history)}>Expand Post</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('HOOKS', history)}>Create Hook</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('CREATE_CTA', history)}>Create CTA</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('IMPROVE_POST', history)}>Improve Post</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('ASSERTIVE', history)}>More Assertive</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('CASUAL', history)}>More Casual</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('FORMAL', history)}>More Formal</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('FIX_GRAMMAR', history)}>Fix Grammar</MenuItem>
                                        <MenuItem onClick={() => handleOptimize('POST_IDEAS', history)}>Post Ideas</MenuItem>
                                    </Menu>
                                </Menu>
                            </Dropdown>
                        </Box>
                    </Box>

                    <Box sx={{ marginTop: 3 }}>

                        <div style={{ position: 'relative' }}>
                            <form onSubmit={handleSubmit}>
                                <Textarea
                                    disabled={loading}
                                    ref={textAreaRef}
                                    minRows={3}
                                    maxRows={4}
                                    name="userInput"
                                    onKeyDown={handleEnter}
                                    value={userInput}
                                    onChange={e => setUserInput(e.target.value)}
                                    placeholder={loading ? "Waiting for response..." : "Type your question..."}
                                    className="chat-input-textarea"
                                    style={{
                                        backgroundColor: 'white',
                                        color: 'black',
                                        padding: '8px', // Add padding to all sides, including right
                                        paddingRight: '110px', // Adjust the right padding for scrollbar margin
                                        boxSizing: 'border-box', // Include padding in textarea's width calculation
                                        marginBottom: '40px', // Add margin to create space below the Textarea
                                    }}
                                />

                                {loading ? (
                                    <CircularProgress variant="solid" style={{
                                        position: 'absolute',
                                        right: '10px',
                                        bottom: '10px',
                                        backgroundColor: 'black',
                                    }} />
                                ) : (
                                    <Button
                                        size="sm"
                                        type="submit"
                                        disabled={loading || optimizeLoading}
                                        style={{
                                            position: 'absolute',
                                            right: '25px',
                                            bottom: '10px',
                                            backgroundColor: 'black',
                                        }}
                                    >
                                        Submit
                                    </Button>
                                )}
                            </form>
                        </div>
                    </Box>

                </Box>
            </Sheet>
        </Modal>
    );
};

export default ModalOptimize;
